import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Lightbulb, 
  Loader2, 
  Sparkles,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Shield,
  TrendingUp,
  Scale,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { RatingRationale } from '@/services/aiService'

interface RatingAssistantProps {
  rationale: RatingRationale | null
  isLoading: boolean
  onGenerateRationale: () => void
  currentRating?: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | null
  onAcceptSuggestion?: (rating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr') => void
}

export function RatingAssistant({ 
  rationale, 
  isLoading, 
  onGenerateRationale,
  currentRating,
  onAcceptSuggestion
}: RatingAssistantProps) {
  const [isCollapsed, setIsCollapsed] = useState(true)

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'exceptional':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'full':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'not-yet-full':
        return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'weak':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getRatingLabel = (rating: string) => {
    switch (rating) {
      case 'exceptional':
        return 'Exceptional'
      case 'full':
        return 'Full/Proficient'
      case 'not-yet-full':
        return 'Not-Yet-Full/Developing'
      case 'weak':
        return 'Weak/Inadequate'
      default:
        return rating
    }
  }

  const getConfidenceColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-blue-600'
    if (score >= 40) return 'text-amber-600'
    return 'text-red-600'
  }

  const getBiasIcon = (type: string) => {
    switch (type) {
      case 'recency':
        return <AlertTriangle className="h-4 w-4" />
      case 'leniency':
      case 'strictness':
        return <Scale className="h-4 w-4" />
      case 'halo':
        return <CheckCircle className="h-4 w-4" />
      case 'horn':
        return <XCircle className="h-4 w-4" />
      default:
        return <AlertTriangle className="h-4 w-4" />
    }
  }

  const getBiasSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'medium':
        return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const ratingDiffers = currentRating && rationale && currentRating !== rationale.suggestedRating

  return (
    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <Shield className="h-5 w-5 text-indigo-600" />
            <CardTitle className="text-indigo-900">AI Impact & Growth Indicator Assistant</CardTitle>
            {rationale && (
              <Badge variant="secondary" className="text-xs">
                Fair & Impact Portfolio-Based
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {rationale && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            )}
            {!rationale && (
              <Button
                size="sm"
                variant="default"
                onClick={onGenerateRationale}
                disabled={isLoading}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analysing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Get AI Impact & Growth Indicator Suggestion
                  </>
                )}
              </Button>
            )}
            {rationale && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onGenerateRationale}
                disabled={isLoading}
                className="text-indigo-600 hover:text-indigo-700"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Collapsed Summary */}
      {rationale && isCollapsed && (
        <CardContent className="pt-3 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge className={`text-base px-4 py-2 ${getRatingColor(rationale.suggestedRating)}`}>
                {getRatingLabel(rationale.suggestedRating)}
              </Badge>
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-gray-600">Confidence: </span>
                  <span className={`font-bold ${getConfidenceColor(rationale.confidenceScore)}`}>
                    {rationale.confidenceScore}%
                  </span>
                  {ratingDiffers && (
                    <>
                      <span className="text-gray-600 mx-2">•</span>
                      <span className="text-amber-700 font-medium">Differs from your indicator</span>
                    </>
                  )}
                </div>
                {rationale.biasWarnings.length > 0 && (
                  <div className="text-red-700 font-medium">
                    {rationale.biasWarnings.length} bias alert{rationale.biasWarnings.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {ratingDiffers && onAcceptSuggestion && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAcceptSuggestion(rationale.suggestedRating)}
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                >
                  <TrendingUp className="h-4 w-4 mr-1" />
                  Accept
                </Button>
              )}
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setIsCollapsed(false)}
                className="text-indigo-600 hover:text-indigo-700"
              >
                View Analysis
              </Button>
            </div>
          </div>
        </CardContent>
      )}

      {/* Expanded Content */}
      {rationale && !isCollapsed && (
        <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
          {/* Suggested Rating with Confidence */}
          <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-indigo-900">AI Suggested Impact & Growth Indicator</h4>
              {ratingDiffers && (
                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">
                  Differs from your indicator
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-4 mb-3">
              <Badge className={`text-lg px-4 py-2 ${getRatingColor(rationale.suggestedRating)}`}>
                {getRatingLabel(rationale.suggestedRating)}
              </Badge>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Confidence:</span>
                <span className={`font-bold text-lg ${getConfidenceColor(rationale.confidenceScore)}`}>
                  {rationale.confidenceScore}%
                </span>
              </div>
            </div>

            {ratingDiffers && onAcceptSuggestion && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAcceptSuggestion(rationale.suggestedRating)}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Update to AI Suggestion
              </Button>
            )}
          </div>

          {/* Evidence-Based Justification */}
          {rationale.evidenceBasedJustification.length > 0 && (
            <div className="space-y-2">
              <h5 className="font-medium text-sm text-indigo-700 flex items-center gap-2">
                <Lightbulb className="h-4 w-4" />
                Impact Portfolio-Based Justification
              </h5>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
                {rationale.evidenceBasedJustification.map((just, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-indigo-600 mt-0.5 font-bold">{i + 1}.</span>
                    <p className="text-sm text-indigo-900">{just}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strengths & Concerns Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Strengths */}
            {rationale.strengthsSupporting.length > 0 && (
              <div className="space-y-2">
                <h5 className="font-medium text-sm text-green-700 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Key Strengths
                </h5>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                  {rationale.strengthsSupporting.map((strength, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">✓</span>
                      <p className="text-sm text-green-900">{strength}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Concerns */}
            {rationale.gapsOrConcerns.length > 0 && (
              <div className="space-y-2">
                <h5 className="font-medium text-sm text-amber-700 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Areas of Concern
                </h5>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                  {rationale.gapsOrConcerns.map((concern, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">⚠</span>
                      <p className="text-sm text-amber-900">{concern}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Bias Warnings */}
          {rationale.biasWarnings.length > 0 && (
            <div className="space-y-2">
              <h5 className="font-medium text-sm text-red-700 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Potential Bias Alerts
              </h5>
              <div className="space-y-2">
                {rationale.biasWarnings.map((bias, i) => (
                  <div 
                    key={i} 
                    className={`border rounded-lg p-3 ${getBiasSeverityColor(bias.severity)}`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      {getBiasIcon(bias.type)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold capitalize">{bias.type} Bias</p>
                          <Badge variant="outline" className="text-xs">
                            {bias.severity} severity
                          </Badge>
                        </div>
                        <p className="text-xs mb-2">{bias.description}</p>
                        <div className="bg-white/50 rounded p-2 mt-2">
                          <p className="text-xs font-medium mb-1">Mitigation:</p>
                          <p className="text-xs">{bias.mitigation}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Calibration Notes */}
          {rationale.calibrationNotes.length > 0 && (
            <div className="space-y-2">
              <h5 className="font-medium text-sm text-purple-700 flex items-center gap-2">
                <Scale className="h-4 w-4" />
                Calibration Notes
              </h5>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
                {rationale.calibrationNotes.map((note, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-purple-600 mt-0.5 font-bold">•</span>
                    <p className="text-sm text-purple-900 italic">{note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Alternative Considerations */}
          {rationale.alternativeRatingConsiderations && (
            <div className="space-y-2">
              <h5 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Alternative Impact & Growth Indicator Considerations
              </h5>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-900">{rationale.alternativeRatingConsiderations}</p>
              </div>
            </div>
          )}

          {/* Timestamp */}
          <div className="text-xs text-gray-500 text-center pt-2 border-t">
            Generated at {new Date(rationale.generatedAt).toLocaleString()}
          </div>
        </CardContent>
      )}

      {!rationale && !isLoading && (
        <CardContent>
          <div className="text-sm text-indigo-700 text-center py-4">
            Get AI-powered indicator suggestion with bias detection and fairness checks
          </div>
        </CardContent>
      )}
    </Card>
  )
}
